import { LiteUser, useInfluencer } from '@client/new/common/LiteUser.mapper';
import { Typography } from '@mui/material';
import { Card } from '@client/new/components/Card';
import {
  Accordion,
  AccordionItem,
} from '@client/new/components/Accordion/Accordion';
import { colors } from '@client/new/components/colors';

import { MetaPixelForm } from './MetaPixelForm';
import { GA4Form } from './GA4Form';
import { TikTokForm } from './TikTokForm';

import { TMap } from '../../../helpers/types';
import { AsyncRenderer } from '../../../components/AsyncRenderer/AsyncRenderer';
import { SourceType } from '../campaign-setup/useURLBuilderState';
import { ReactComponent as MetaLogo } from './icons/Meta.svg';
import { ReactComponent as GALogo } from './icons/GoogleServices.svg';
import { ReactComponent as TikTokLogo } from './icons/TikTok.svg';
import { useEffect } from 'react';
import { trackEvent } from '@services/tracking';

export const logoMapper: TMap<ConfigurableSourceType, React.ReactNode> = {
  facebook: <MetaLogo />,
  google: <GALogo />,
  tiktok: <TikTokLogo />,
};
type Trim<T, U extends T> = T extends U ? T : never;
export type ConfigurableSourceType = Trim<
  SourceType,
  'facebook' | 'tiktok' | 'google'
>;

type AccordionTitleProps = {
  logo: React.ReactNode;
  title: string;
  subTitle: string;
};

const AccordionTitle = (p: AccordionTitleProps) => (
  <div className="flex gap-3">
    <div className="p-2 flex justify-center items-center" style={{ width: 53 }}>
      {p.logo}
    </div>
    <div className="flex flex-col gap-1">
      <Typography variant="body1" fontWeight={700} color={colors.text900}>
        {p.title}
      </Typography>
      <Typography variant="body2" color={colors.primaryDark400}>
        {p.subTitle}
      </Typography>
    </div>
  </div>
);

type IntegrationsProps = {
  creator: LiteUser;
  sourceType?: ConfigurableSourceType;
};
const sourcTypeIndexMap: TMap<ConfigurableSourceType, number> = {
  facebook: 0,
  tiktok: 1,
  google: 2,
};
export const Integrations = (p: IntegrationsProps) => {
  const asyncCreator = useInfluencer(p.creator.id);
  useEffect(() => {
    trackEvent({
      type: 'Integrations Viewed',
      payload: {},
    });
  }, []);

  return (
    <AsyncRenderer
      data={asyncCreator}
      renderer={(creator) => (
        <div className="flex-1">
          <Accordion
            initialActiveIndexes={
              p.sourceType ? [sourcTypeIndexMap[p.sourceType]] : []
            }
          >
            <Typography variant="h5">Ad Tracking</Typography>

            <Card variant="basic" rounded="2xl" className="px-5 pt-5">
              <AccordionItem
                index={0}
                iconWidth={20}
                title={
                  <AccordionTitle
                    title="Meta"
                    subTitle="Track performance of your Facebook & Instagram ads"
                    logo={logoMapper['facebook']}
                  />
                }
              >
                <MetaPixelForm creator={creator} />
              </AccordionItem>
            </Card>

            <Card variant="basic" rounded="2xl" className="px-5 pt-5">
              <AccordionItem
                index={1}
                iconWidth={20}
                title={
                  <AccordionTitle
                    title="TikTok Pixel"
                    subTitle="Track performance of your TikTok ads"
                    logo={logoMapper['tiktok']}
                  />
                }
              >
                <TikTokForm creator={creator} />
              </AccordionItem>
            </Card>
            <Card variant="basic" rounded="2xl" className="px-5 pt-5">
              <AccordionItem
                index={2}
                iconWidth={20}
                title={
                  <AccordionTitle
                    title="YouTube Ads, Google Ads & Google Analytics"
                    subTitle="Track performance of your Google ads, YouTube ads & Web traffic"
                    logo={logoMapper['google']}
                  />
                }
              >
                <GA4Form creator={creator} />
              </AccordionItem>
            </Card>
          </Accordion>
        </div>
      )}
    />
  );
};
